import { type PaginationParams } from "features/Library/Common/models";
import { type PerformanceFilter } from "features/Library/Common/utils/performanceUtils";
import { peopleReportingService } from "features/Reporting/services/peopleReportingService";
import { type FetchPageResult } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { type AcquisitionCampaign, type PeopleAcquisitionDetails } from "features/Reporting/types/people";

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;

export type PeopleAcquisitionDrilldownFilters = {
  filter: PerformanceFilter;
  selectedCampaign: string;
  selectedMedium: string;
  selectedSource: string;
};

export type AcquisitionCampaignRow = AcquisitionCampaign & {
  id: string;
  Campaign: string;
  Medium: string;
  Source: string;
};

export const fetchPeopleAcquisitionDetailsPage = async (
  filters: PeopleAcquisitionDrilldownFilters,
  pagination: PaginationParams,
  signal?: AbortSignal,
): Promise<FetchPageResult<PeopleAcquisitionDetails>> => {
  const { filter, selectedCampaign, selectedMedium, selectedSource } = filters;

  const response = await peopleReportingService.acquisition.getPeopleAcquisitionDetails(
    {
      ...filter,
      campaign: selectedCampaign,
      medium: selectedMedium,
      source: selectedSource,
    },
    pagination,
    signal,
  );

  const rows = response.data.map((detail) => ({ ...detail, id: `${detail.UserId}-${detail.ClickedAt}` }));
  const totalCount = Number.parseInt(response.headers[countHeaderName]);

  return {
    rows,
    totalCount,
  };
};

export const fetchAcquisitionCampaignsPaginated = async (
  filter: PerformanceFilter,
  pagination?: PaginationParams,
  signal?: AbortSignal,
): Promise<FetchPageResult<AcquisitionCampaignRow>> => {
  const response = await peopleReportingService.acquisition.getCampaigns(filter, pagination, signal);
  const skip = pagination?.skip ?? 0;

  const rows = response.data.map((item) => {
    const Campaign = item.Campaign || "Unspecified";
    const Medium = item.Medium || "Unspecified";

    return {
      ...item,
      Campaign,
      Medium,
      id: `${skip}-${Campaign}-${Medium}-${item.Source}-${item.LastActivity}`,
    };
  });

  return {
    rows,
    totalCount: Number.parseInt(response.headers[countHeaderName]),
  };
};
